import { skipToken } from "@reduxjs/toolkit/query";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { isNotificationLoadIssue, NotificationLoadIssue } from "notifications/_stores/notifications-redux-slice";
import type { NotificationType } from "notifications/notification-type";
import TopicCategory from "notifications/topics/topic-category";
import { useMemo } from "react";
import { useGetWorkAgreementsByIdsQuery } from "work/work-agreements/_api/work-agreement-api";
import type BarebonesWorkAgreementResponse from "work/work-agreements/_api/responses/barebones-work-agreement-response";
import WorkVersion from "work/work-agreements/versions/work-version";

type NotificationPage = PaginatedResponse<NotificationLoadIssue | NotificationType> | undefined;

/** What a row says about the work agreement it is about. */
export type NotificationWorkAgreement = Readonly<{
  name: string;
  /**
   * The agreement's version, "v1" or "v2", apart from the name so the row can cut a long name short
   * and still show it: a change order keeps its project's name, and the rows about v1 and v2 read
   * the same without it (#6809).
   */
  version?: string;
}>;

/**
 * The name of each work agreement the given notifications are about, keyed by work agreement id.
 *
 * Every work agreement row's type label reads "Work Agreement", and the agreement's name sits
 * somewhere inside the Description text, which that column clips. So a row names its agreement
 * under the label, the way a proposal row names its proposal (#6854). The rows carry the agreement
 * by topic id alone, so the names are fetched for the page being shown, the way the proposals are.
 *
 * Nothing is requested for a page with no work agreement rows on it.
 */
export default function useNotificationWorkAgreements(
  tableNotifications: NotificationPage,
): ReadonlyMap<string, NotificationWorkAgreement> {
  const workAgreementIds = useMemo(
    () => getNotificationWorkAgreementTopicIds(tableNotifications),
    [tableNotifications],
  );
  const workAgreementQuery = useGetWorkAgreementsByIdsQuery(
    workAgreementIds.length ? { workAgreementIds } : skipToken,
  );

  return useMemo(
    () => getNotificationWorkAgreementsById(workAgreementQuery.data?.found),
    [workAgreementQuery.data],
  );
}

/**
 * The work agreements the rows on screen are about, deduplicated. Rows that failed to load, and rows
 * whose topic is not a work agreement, name nothing to fetch.
 */
export function getNotificationWorkAgreementTopicIds(tableNotifications: NotificationPage): string[] {
  if (!tableNotifications) return [];

  const workAgreementIds = new Set<string>();
  tableNotifications.data.forEach((notification) => {
    if (isNotificationLoadIssue(notification)) return;
    if (notification.topic?.category !== TopicCategory.Work.Agreement) return;

    const topicId = notification.topic?.id?.value;
    if (topicId) workAgreementIds.add(topicId);
  });

  return [...workAgreementIds];
}

/**
 * The name and version of each work agreement that came back. An agreement the reader may not open
 * is listed as unavailable rather than found, so its row names nothing — a row names its agreement
 * only where the agreement itself could have been opened.
 */
export function getNotificationWorkAgreementsById(
  workAgreements: BarebonesWorkAgreementResponse[] | undefined,
): ReadonlyMap<string, NotificationWorkAgreement> {
  const byId = new Map<string, NotificationWorkAgreement>();

  for (const workAgreement of workAgreements ?? []) {
    if (!workAgreement.id || !workAgreement.name) continue;
    byId.set(workAgreement.id, {
      name: workAgreement.name,
      version: WorkVersion.fromNumbers(workAgreement)?.label,
    });
  }

  return byId;
}
